// В.2.h-rebalance: утилита «эвакуация юнита в руку» для активки Арианы.
//
// Снимает юнита с поля, кладёт в руку владельца. Юнит «перерождается»:
//   - current ← base (HP, atk, armor — с учётом постоянных модификаций ниже).
//   - Временные баффы (applyTemporaryBuff) — снимаются.
//   - Моды Синтетика (modifications[]) — детачатся (с откатом дельт).
//   - Постоянные баффы (applyPermanentBuff) — переносятся на «свежий»
//     инстанс через resetCardToTemplate-like обнуление + повторное
//     применение списка card.permanentBuffs.
//   - Постоянные keyword'ы (addPermanentKeyword) — переносятся.
//   - Молчание — снимается (юнит «обновлён»).
//   - Posthumous контракт, sleeperState и прочее состояние — сброс.
//   - placedTurn = null, revealed=false (ровно как карта в руке).
//
// Ограничения:
//   - Если на поле нет своих юнитов → активка не срабатывает (вызывающий
//     проверяет это раньше).
//   - Лимит руки 10: если рука переполнена, эвакуированный юнит уходит в
//     сброс (стандартный fallback по правилу 6.2 для возврата в руку).
//
// API:
//   evacuateUnitToHand(state, ownerIdx, unit, sourceLabel)
//   → { ok, toHand, burned }   — toHand=true если ушёл в руку, burned=true
//                                если переполнение → discard.
//
// Реализация делегирует в существующую инфру (Офицер связи 1-я линия делает
// то же самое возвратом в руку). Чтобы не дублировать код, повторяем паттерн
// resetCardToTemplate, но СОХРАНЯЕМ permanentBuffs / persistentKeywords.

import { detachMod } from '../syntetics/modifications.js';
import { applyPermanentBuff } from '../../buffs.js';
import { addPermanentKeyword } from '../../keywords/permanent.js';
// C.1: Цепь командования удалена.
import { recomputeLineTriggers, clearDerivedKeywordsFor } from '../../keywords/line-triggers.js';
import { onUnitLeftField } from '../deus/hooks.js';
// Этап 23: шина событий «карта вернулась в руку».
import { fireCardReturnedToHand } from '../../events/card-returned.js';

export function evacuateUnitToHand(state, ownerIdx, unit, sourceLabel = 'Эвакуация') {
  if (!unit) return { ok: false, reason: 'no_target' };
  const owner = state.players[ownerIdx];
  if (!owner) return { ok: false, reason: 'no_owner' };
  if (unit.ownerId !== ownerIdx) return { ok: false, reason: 'not_own' };
  if (unit.type !== 'unit') return { ok: false, reason: 'not_unit' };

  // Снимок постоянных баффов / keyword'ов ДО reset'а.
  const savedBuffs = Array.isArray(unit.permanentBuffs) ? [...unit.permanentBuffs] : [];
  const savedKeywords = Array.isArray(unit.persistentKeywords) ? [...unit.persistentKeywords] : [];

  // Снимаем Моды (с откатом дельт + триггеров деатча).
  while (Array.isArray(unit.modifications) && unit.modifications.length > 0) {
    detachMod(state, unit, unit.modifications.length - 1);
  }
  // Снимаем с поля.
  if (unit.line !== null && unit.slot !== null) {
    if (owner.field[unit.line][unit.slot] === unit) {
      owner.field[unit.line][unit.slot] = null;
    }
  }
  clearDerivedKeywordsFor(unit);

  // Полный сброс к шаблону.
  unit.base.atk   = unit.template?.atk   ?? unit.base.atk;
  unit.base.armor = unit.template?.armor ?? unit.base.armor;
  unit.base.hp    = unit.template?.hp    ?? unit.base.hp;
  if (Array.isArray(unit.permanentBuffs)) unit.permanentBuffs.length = 0;
  if (unit.silenceSources && typeof unit.silenceSources.clear === 'function') {
    unit.silenceSources.clear();
  }
  unit.current.atk    = unit.base.atk;
  unit.current.armor  = unit.base.armor;
  unit.current.hp     = unit.base.hp;
  unit.persistentArmor = 0;
  unit.persistentMaxArmor = 0;
  unit.tempArmorBands = [];
  unit.directorAuraApplied = { atk: 0, armor: 0, hp: 0 };
  unit.chainOfCommandApplied = { atk: 0, maxHp: 0 };
  unit.likvidatorEkspertBonus = 0;
  if (Array.isArray(unit.derivedKeywords))    unit.derivedKeywords.length = 0;
  if (Array.isArray(unit.persistentKeywords)) unit.persistentKeywords.length = 0;
  if (Array.isArray(unit.temporaryBuffs))     unit.temporaryBuffs.length = 0;
  unit.placedTurn = null;
  unit.revealed = false;
  unit.activated = false;
  unit.side = 'owner';
  unit.spawnPassiveFired = false;
  unit.orderFiredAtSpawn = false;
  unit.orderFiredThisTurn = false;
  unit.counterAttackedThisPhase = false;
  unit.heroAttackedThisTurn = false;
  unit.kustarnyyStimulator = null;
  unit.sleeperState = null;
  if (unit.current) unit.current.silenced = false;
  unit.stealthSuppressed = false;
  unit.stealthUsed = false;
  unit.attackPriority = null;
  unit.healPriority = null;
  unit.overloaded = false;
  unit.posthumousContractUsed = false;
  // Сбросим keywords к template'ным.
  if (Array.isArray(unit.template?.keywords)) {
    unit.keywords = [...unit.template.keywords];
  } else {
    unit.keywords = [];
  }
  unit.alive = true;
  unit.line = null;
  unit.slot = null;

  // Хук «ушёл с поля» — пересчитать ауры и т.п.
  onUnitLeftField(state, ownerIdx, unit);

  // Возвращаем сохранённые постоянные баффы (свежий накат на чистый base).
  for (const b of savedBuffs) {
    applyPermanentBuff(state, unit, { atk: b.atk | 0, armor: b.armor | 0, hp: b.hp | 0 },
      b.id || 'preserved', b.label || sourceLabel);
  }
  for (const kw of savedKeywords) {
    addPermanentKeyword(state, unit, kw, 'preserved', sourceLabel);
  }

  // В руку (с учётом лимита 10).
  let burned = false;
  if (owner.hand.length >= 10) {
    owner.discard.push(unit);
    burned = true;
    state.log?.add(
      `🪖 ${sourceLabel}: рука [${owner.name}] полна — "${unit.name_ru}" сгорает`
    );
  } else {
    owner.hand.push(unit);
    state.log?.add(
      `🪖 ${sourceLabel} → "${unit.name_ru}" возвращён в руку [${owner.name}]`
    );
  }

  // C.1: пересчёт линейных триггеров. Цепь командования удалена.
  recomputeLineTriggers(state);

  // Этап 23: эмитим событие «карта вернулась в руку» только если она
  // действительно осела в руке (не сгорела).
  if (!burned) {
    fireCardReturnedToHand(state, unit);
  }

  return { ok: true, toHand: !burned, burned };
}
